import React, { useEffect, useMemo, useState } from "react";
import { getLinkedInAccounts } from "../../services/apiService";
import { accountNeedsAttention, getAccountLimits } from "../linkedinAccounts/accountState";

export default function SendersStep({ state, setState }) {
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getLinkedInAccounts({ limit: 100 })
      .then((res) => {
        const data = res.data?.data || res.data?.accounts || res.data || [];
        setAccounts(Array.isArray(data) ? data : []);
      })
      .catch(() => setAccounts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) =>
      (a.accountName || a.firstName || "").toLowerCase().includes(q),
    );
  }, [accounts, search]);

  const selected = new Set(state.linkedinAccountIds || []);

  const toggle = (id) => {
    setState((prev) => {
      const next = new Set(prev.linkedinAccountIds || []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      const linkedinAccountIds = Array.from(next);
      return {
        ...prev,
        linkedinAccountIds,
        linkedinAccountId: linkedinAccountIds[0] || null,
        primaryLinkedInAccountId: linkedinAccountIds[0] || null,
      };
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-800">
        Selecting multiple senders will allow you to increase the daily sending volume, allowing you
        to reach 2x, 3x, 10x more prospects per day, at no risk.
      </div>
      <div className="flex gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search senders"
          className="flex-1 border rounded-xl px-4 py-2 text-sm"
        />
      </div>
      <div className="border rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500">
          <div className="col-span-1" />
          <div className="col-span-4">Name</div>
          <div className="col-span-3">Activity</div>
          <div className="col-span-4">Daily sending limits</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-gray-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-400">No LinkedIn accounts found</div>
        ) : (
          filtered.map((account) => (
            <label
              key={account.id}
              className="grid grid-cols-12 gap-2 px-4 py-3 border-t items-center hover:bg-gray-50 cursor-pointer"
            >
              <div className="col-span-1">
                <input
                  type="checkbox"
                  checked={selected.has(account.id)}
                  onChange={() => toggle(account.id)}
                />
              </div>
              <div className="col-span-4 flex items-center gap-2 min-w-0">
                {account.profileImage ? (
                  <img
                    src={account.profileImage}
                    alt=""
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-200" />
                )}
                <span className="text-sm font-medium truncate">
                  {account.accountName || `${account.firstName || ""} ${account.lastName || ""}`}
                </span>
              </div>
              <div className="col-span-3">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    !accountNeedsAttention(account)
                      ? "bg-green-100 text-green-700"
                      : "bg-orange-100 text-orange-700"
                  }`}
                  title={account.connectionError || undefined}
                >
                  {!accountNeedsAttention(account) ? "✓ Available" : "Needs attention"}
                </span>
              </div>
              <div className="col-span-4 text-xs text-gray-600 flex gap-3">
                <span>👤 {getAccountLimits(account).connectionsUsed}/{getAccountLimits(account).connectionsTotal}</span>
                <span>✉ {getAccountLimits(account).messagesUsed}/{getAccountLimits(account).messagesTotal}</span>
              </div>
            </label>
          ))
        )}
      </div>
    </div>
  );
}
